import { Autocomplete, Box, Button, Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Radio, RadioGroup, Select, Switch, TextField, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Popper, Paper } from '@mui/material';

// Sub-component for text input fields
export function TextInput({ name, label, value, onChange, error }) {
  return <TextField id={name} label={label} value={value} onChange={(e) => onChange(e.target.value)} error={!!error} helperText={error} fullWidth variant="outlined" margin="normal" />;
}

// Sub-component for textarea fields
export function TextArea({ name, label, value, onChange, error }) {
  return <TextField id={name} label={label} value={value} onChange={(e) => onChange(e.target.value)} error={!!error} helperText={error} fullWidth variant="outlined" margin="normal" multiline rows={4} />;
}

// Sub-component for select fields
export function SelectField({ name, label, options, value, onChange, error, isMulti = false }) {
  return (
    <FormControl fullWidth margin="normal" error={!!error}>
      <InputLabel id={`${name}-label`}>{label}</InputLabel>
      <Select id={name} labelId={`${name}-label`} value={value} onChange={(e) => onChange(e.target.value)} multiple={isMulti}>
        {options.map((option, idx) => (
          <MenuItem key={idx} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// Sub-component for date fields
export function DateField({ name, label, value, onChange, error, type = 'date' }) {
  return (
    <TextField
      id={name}
      label={label}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={!!error}
      helperText={error}
      fullWidth
      variant="outlined"
      margin="normal"
      InputLabelProps={{
        shrink: true
      }}
    />
  );
}

// Sub-component for time fields
export function TimeField({ name, label, value, onChange, error }) {
  return (
    <TextField
      id={name}
      label={label}
      type="time"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={!!error}
      helperText={error}
      fullWidth
      variant="outlined"
      margin="normal"
      InputLabelProps={{
        shrink: true
      }}
    />
  );
}

// Sub-component for date range fields
export function DateRangeField({ name, label, value, onChange, error, gridSize = 12 }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(value || ['', '']);
  const [tempRange, setTempRange] = useState(['', '']); // משתנה לבדיקה אם שני הערכים היו מלאים בהתחלה
  const wrapperRef = useRef(null);
  const inputGeneralRef = useRef(null);
  const inputStartRef = useRef(null);
  const inputEndRef = useRef(null);
  const inputTestRef = useRef(null);

  const [isRefReady, setIsRefReady] = useState(false);
  const [initialFilled, setInitialFilled] = useState(false); // משתנה לבדיקה אם שני הערכים היו מלאים בהתחלה

  useEffect(() => {
    const interval = setInterval(() => {
      if (inputStartRef?.current && !isRefReady) {
        console.log('Ref is ready:', inputStartRef?.current);
        inputStartRef?.current?.focus();
        setIsRefReady(true);
      }
      if (!inputStartRef?.current) {
        setIsRefReady(false);
      }
    }, 0); // בדיקה כל 100ms
    return () => clearInterval(interval); // ניקוי ה-interval
  }, [isRefReady]); // תלות במצב של isRefReady

  const handleOpen = (event) => {
    console.log('Opening Popper');
    setAnchorEl(event?.currentTarget);
    setOpen(true);
    // בדיקה אם שני הערכים כבר מלאים בעת פתיחה
    if (range[0] && range[1]) {
      setInitialFilled(true);
    } else {
      setInitialFilled(false);
    }
    setTimeout(() => {
      inputStartRef?.current?.focus();
    }, 0);
  };

  const handleDateChange = (date, index) => {
    const newRange = [...range];
    const newTempRange = initialFilled && tempRange[0] && tempRange[1] ? ['', ''] : [...tempRange];
    newRange[index] = date;

    if (initialFilled) {
      newTempRange[index] = date;
    }

    // בדיקות תקינות בין תחילה לסוף
    if (index === 0 && newRange[1] && newRange[1] < date) {
      newRange[1] = ''; // Reset end date if it's earlier than start date
      newTempRange[1] = ''; // Reset end date if it's earlier than start date
      inputEndRef?.current?.focus();
    }
    if (index === 1 && newRange[0] && date < newRange[0]) {
     newRange[0] = ''; // Reset start date if it's later than end date
     newTempRange[0] = ''; // Reset start date if it's later than end date
    }

    setRange(newRange);
    setTempRange(newTempRange);
    onChange(newRange);

    // אם שני הערכים מלאים, סגור את התפריט רק אם לא היו מלאים בתחילה
    if (newRange[0] && newRange[1] && !initialFilled) {
      handleClose();
    }
    if (initialFilled && newTempRange[0] && newTempRange[1]) {
      handleClose();
    }
  };

  const handleClose = () => {
    console.log('Closing Popper');
    setOpen(false);
  };

  const handleClickOutside = (event) => {
    if (wrapperRef?.current && !wrapperRef?.current?.contains(event.target) && !inputGeneralRef?.current?.contains(event.target) && !inputStartRef?.current?.contains(event.target) && !inputEndRef?.current?.contains(event.target)) {
      handleClose();
    }
  };

  const handleTabNavigation = (event) => {
    if (event.key === 'Tab') {
      if (document.activeElement === inputGeneralRef.current) {
        event.preventDefault();
        inputStartRef.current?.focus();
      } else if (document.activeElement === inputStartRef.current) {
        event.preventDefault();
        inputEndRef.current?.focus();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleTabNavigation);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleTabNavigation);
    };
  }, []);

  const calculateFieldWidth = () => {
    if (gridSize > 6) {
      return '50%';
    } else if (gridSize > 3) {
      return '100%';
    } else {
      return '100%';
    }
  };

  return (
    <Box ref={wrapperRef}>
      <Typography>{label}</Typography>
      <TextField ref={inputGeneralRef} value={range[0] && range[1] ? `${range[0]} - ${range[1]}` : ''} onClick={handleOpen} fullWidth readOnly error={!!error} helperText={error} sx={{ minWidth: 200 }} />
      <Popper ref={inputTestRef} open={open} anchorEl={anchorEl} placement="bottom-start">
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            gap: 2,
            flexDirection: gridSize > 6 ? 'row' : 'column',
            minWidth: Math.max(anchorEl?.offsetWidth || 120, 120)
          }}>
          <TextField
            inputRef={inputStartRef} // שימוש ב-inputRef כדי לגשת ישירות ל-input
            label="Start Date"
            type="date"
            value={range[0]}
            onChange={(e) => handleDateChange(e.target.value, 0)}
            sx={{
              width: calculateFieldWidth(),
              InputProps: { inputProps: { min: null, max: range[1] } },
              InputLabelProps: { shrink: true }
            }}
            InputLabelProps={{
              shrink: true
            }}
          />
          <TextField
            inputRef={inputEndRef}
            label="End Date"
            type="date"
            value={range[1]}
            onChange={(e) => handleDateChange(e.target.value, 1)}
            sx={{
              width: calculateFieldWidth(),
              InputProps: { inputProps: { min: range[0], max: null } },
              InputLabelProps: { shrink: true }
            }}
            InputLabelProps={{
              shrink: true
            }}
          />
        </Paper>
      </Popper>
    </Box>
  );
}
export function DateRangeField10({ name, label, value, onChange, error, gridSize = 12 }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(value || ['', '']);
  const wrapperRef = useRef(null);
  const inputGeneralRef = useRef(null);
  const inputStartRef = useRef(null);
  const inputEndRef = useRef(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
    setTimeout(() => {
      inputStartRef.current?.focus();
    }, 0);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDateChange = (date, index) => {
    const newRange = [...range];
    newRange[index] = date;
    if (index === 0 && newRange[1] && newRange[1] < date) {
      newRange[1] = ''; // Reset end date if it's earlier than start date
    }
    if (index === 1 && newRange[0] && date < newRange[0]) {
      newRange[0] = ''; // Reset start date if it's later than end date
    }
    setRange(newRange);
    onChange(newRange);

    if (index === 0) {
      setTimeout(() => {
        inputEndRef.current?.focus();
      }, 0);
    }
  };

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current?.contains(event.target) && !inputGeneralRef.current?.contains(event.target) && !inputStartRef.current?.contains(event.target) && !inputEndRef.current?.contains(event.target)) {
      handleClose();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const calculateFieldWidth = () => {
    if (gridSize > 6) {
      return '50%';
    } else if (gridSize > 3) {
      return '100%';
    } else {
      return '100%';
    }
  };

  return (
    <Box ref={wrapperRef}>
      <Typography>{label}</Typography>
      <TextField ref={inputGeneralRef} value={range[0] && range[1] ? `${range[0]} - ${range[1]}` : ''} onClick={handleOpen} fullWidth readOnly error={!!error} helperText={error} sx={{ minWidth: 200 }} />
      <Popper open={open} anchorEl={anchorEl} placement="bottom-start">
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            gap: 2,
            flexDirection: gridSize > 6 ? 'row' : 'column',
            minWidth: Math.max(anchorEl?.offsetWidth || 200, 200)
          }}>
          <TextField
            ref={inputStartRef}
            label="Start Date"
            type="date"
            value={range[0]}
            onChange={(e) => handleDateChange(e.target.value, 0)}
            sx={{
              width: calculateFieldWidth(),
              InputProps: { inputProps: { min: null, max: range[1] } },
              InputLabelProps: { shrink: true }
            }}
            InputLabelProps={{
              shrink: true
            }}
          />
          <TextField
            ref={inputEndRef}
            label="End Date"
            type="date"
            value={range[1]}
            onChange={(e) => handleDateChange(e.target.value, 1)}
            sx={{
              width: calculateFieldWidth(),
              InputProps: { inputProps: { min: range[0], max: null } },
              InputLabelProps: { shrink: true }
            }}
            InputLabelProps={{
              shrink: true
            }}
          />
        </Paper>
      </Popper>
    </Box>
  );
}
export function DateRangeField9({ name, label, value, onChange, error, gridSize = 12 }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(value || ['', '']);
  const wrapperRef = useRef(null);
  const inputGeneralRef = useRef(null);
  const inputStartRef = useRef(null);
  const inputEndRef = useRef(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
    inputStartRef.current?.focus();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDateChange = (date, index) => {
    const newRange = [...range];
    newRange[index] = date;
    if (index === 0 && newRange[1] && newRange[1] < date) {
      newRange[1] = ''; // Reset end date if it's earlier than start date
    }
    if (index === 1 && newRange[0] && date < newRange[0]) {
      newRange[0] = ''; // Reset start date if it's later than end date
    }
    setRange(newRange);
    onChange(newRange);

    if (index === 0) {
      inputEndRef.current?.focus();
    }
  };

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current?.contains(event.target) && !inputGeneralRef.current?.contains(event.target) && !inputStartRef.current?.contains(event.target) && !inputEndRef.current?.contains(event.target)) {
      handleClose();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const calculateFieldWidth = () => {
    if (gridSize > 6) {
      return '50%';
    } else if (gridSize > 3) {
      return '100%';
    } else {
      return '100%';
    }
  };

  return (
    <Box ref={wrapperRef}>
      <Typography>{label}</Typography>
      <TextField ref={inputGeneralRef} value={range[0] && range[1] ? `${range[0]} - ${range[1]}` : ''} onClick={handleOpen} fullWidth readOnly error={!!error} helperText={error} sx={{ minWidth: 200 }} />
      <Popper open={open} anchorEl={anchorEl} placement="bottom-start">
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            gap: 2,
            flexDirection: gridSize > 6 ? 'row' : 'column',
            minWidth: Math.max(anchorEl?.offsetWidth || 200, 200)
          }}>
          <TextField
            ref={inputStartRef}
            label="Start Date"
            type="date"
            value={range[0]}
            onChange={(e) => handleDateChange(e.target.value, 0)}
            sx={{
              width: calculateFieldWidth(),
              InputProps: { inputProps: { min: null, max: range[1] } },
              InputLabelProps: { shrink: true }
            }}
          />
          <TextField
            ref={inputEndRef}
            label="End Date"
            type="date"
            value={range[1]}
            onChange={(e) => handleDateChange(e.target.value, 1)}
            sx={{
              width: calculateFieldWidth(),
              InputProps: { inputProps: { min: range[0], max: null } },
              InputLabelProps: { shrink: true }
            }}
          />
        </Paper>
      </Popper>
    </Box>
  );
}
export function DateRangeField8({ name, label, value, onChange, error, gridSize = 12 }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(value || ['', '']);
  const wrapperRef = useRef(null);
  const inputGeneralRef = useRef(null);
  const inputStartRef = useRef(null);
  const inputEndRef = useRef(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDateChange = (date, index) => {
    const newRange = [...range];
    newRange[index] = date;
    if (index === 0 && newRange[1] && newRange[1] < date) {
      newRange[1] = ''; // Reset end date if it's earlier than start date
    }
    if (index === 1 && newRange[0] && date < newRange[0]) {
      newRange[0] = ''; // Reset start date if it's later than end date
    }
    setRange(newRange);
    onChange(newRange);
  };

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current?.contains(event.target) && !inputGeneralRef.current?.contains(event.target) && !inputStartRef.current?.contains(event.target) && !inputEndRef.current?.contains(event.target)) {
      handleClose();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const calculateFieldWidth = () => {
    if (gridSize > 6) {
      return '50%';
    } else if (gridSize > 3) {
      return '100%';
    } else {
      return '100%';
    }
  };

  return (
    <Box ref={wrapperRef}>
      <Typography>{label}</Typography>
      <TextField ref={inputGeneralRef} value={range[0] && range[1] ? `${range[0]} - ${range[1]}` : ''} onClick={handleOpen} fullWidth readOnly error={!!error} helperText={error} sx={{ minWidth: 200 }} />
      <Popper open={open} anchorEl={anchorEl} placement="bottom-start">
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            gap: 2,
            flexDirection: gridSize > 6 ? 'row' : 'column',
            minWidth: Math.max(anchorEl?.offsetWidth || 200, 200)
          }}>
          <TextField
            ref={inputStartRef}
            label="Start Date"
            type="date"
            value={range[0]}
            onChange={(e) => handleDateChange(e.target.value, 0)}
            sx={{
              width: calculateFieldWidth(),
              InputProps: { inputProps: { min: null, max: range[1] } },
              InputLabelProps: { shrink: true }
            }}
          />
          <TextField
            ref={inputEndRef}
            label="End Date"
            type="date"
            value={range[1]}
            onChange={(e) => handleDateChange(e.target.value, 1)}
            sx={{
              width: calculateFieldWidth(),
              InputProps: { inputProps: { min: range[0], max: null } },
              InputLabelProps: { shrink: true }
            }}
          />
        </Paper>
      </Popper>
    </Box>
  );
}
export function DateRangeField7({ name, label, value, onChange, error }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(value || ['', '']);
  const wrapperRef = useRef(null);
  const inputGeneralRef = useRef(null);
  const inputStartRef = useRef(null);
  const inputEndRef = useRef(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDateChange = (date, index) => {
    const newRange = [...range];
    newRange[index] = date;
    if (index === 0 && newRange[1] && newRange[1] < date) {
      newRange[1] = ''; // Reset end date if it's earlier than start date
    }
    if (index === 1 && newRange[0] && date < newRange[0]) {
      newRange[0] = ''; // Reset start date if it's later than end date
    }
    setRange(newRange);
    onChange(newRange);
  };

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current?.contains(event.target) && !inputGeneralRef.current?.contains(event.target) && !inputStartRef.current?.contains(event.target) && !inputEndRef.current?.contains(event.target)) {
      handleClose();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Box ref={wrapperRef}>
      <Typography>{label}</Typography>
      <TextField ref={inputGeneralRef} value={range[0] && range[1] ? `${range[0]} - ${range[1]}` : ''} onClick={handleOpen} fullWidth readOnly error={!!error} helperText={error} sx={{ minWidth: 200 }} />
      <Popper open={open} anchorEl={anchorEl} placement="bottom-start">
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            gap: 2,
            flexDirection: 'column',
            minWidth: Math.max(anchorEl?.offsetWidth || 200, 200)
          }}>
          <TextField
            ref={inputStartRef}
            label="Start Date"
            type="date"
            value={range[0]}
            onChange={(e) => handleDateChange(e.target.value, 0)}
            fullWidth
            InputProps={{ inputProps: { min: null, max: range[1] } }}
            InputLabelProps={{
              shrink: true
            }}
          />
          <TextField
            ref={inputEndRef}
            label="End Date"
            type="date"
            value={range[1]}
            onChange={(e) => handleDateChange(e.target.value, 1)}
            fullWidth
            InputProps={{ inputProps: { min: range[0], max: null } }}
            InputLabelProps={{
              shrink: true
            }}
          />
        </Paper>
      </Popper>
    </Box>
  );
}
export function DateRangeField6({ name, label, value, onChange, error }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(value || ['', '']);
  const wrapperRef = useRef(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDateChange = (date, index) => {
    const newRange = [...range];
    newRange[index] = date;
    if (index === 0 && newRange[1] && newRange[1] < date) {
      newRange[1] = ''; // Reset end date if it's earlier than start date
    }
    if (index === 1 && newRange[0] && date < newRange[0]) {
      newRange[0] = ''; // Reset start date if it's later than end date
    }
    setRange(newRange);
    onChange(newRange);
  };

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      handleClose();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Box ref={wrapperRef}>
      <Typography>{label}</Typography>
      <TextField value={range[0] && range[1] ? `${range[0]} - ${range[1]}` : ''} onClick={handleOpen} fullWidth readOnly error={!!error} helperText={error} sx={{ minWidth: 200 }} />
      <Popper open={open} anchorEl={anchorEl} placement="bottom-start">
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            gap: 2,
            flexDirection: 'column',
            minWidth: Math.max(anchorEl?.offsetWidth || 200, 200)
          }}>
          <TextField label="Start Date" type="date" value={range[0]} onChange={(e) => handleDateChange(e.target.value, 0)} fullWidth InputProps={{ inputProps: { min: null, max: range[1] } }} />
          <TextField label="End Date" type="date" value={range[1]} onChange={(e) => handleDateChange(e.target.value, 1)} fullWidth InputProps={{ inputProps: { min: range[0], max: null } }} />
        </Paper>
      </Popper>
    </Box>
  );
}
export function DateRangeField5({ name, label, value, onChange, error }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(value || ['', '']);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDateChange = (date, index) => {
    const newRange = [...range];
    newRange[index] = date;
    if (index === 0 && newRange[1] && newRange[1] < date) {
      newRange[1] = ''; // Reset end date if it's earlier than start date
    }
    if (index === 1 && newRange[0] && date < newRange[0]) {
      newRange[0] = ''; // Reset start date if it's later than end date
    }
    setRange(newRange);
    onChange(newRange);
  };

  return (
    <Box>
      <Typography>{label}</Typography>
      <TextField value={range[0] && range[1] ? `${range[0]} - ${range[1]}` : ''} onClick={handleOpen} fullWidth readOnly error={!!error} helperText={error} sx={{ minWidth: 200 }} />
      <Popper open={open} anchorEl={anchorEl} placement="bottom-start">
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            gap: 2,
            flexDirection: 'column',
            minWidth: Math.max(anchorEl?.offsetWidth || 200, 200)
          }}>
          <TextField label="Start Date" type="date" value={range[0]} onChange={(e) => handleDateChange(e.target.value, 0)} fullWidth InputProps={{ inputProps: { min: null, max: range[1] } }} />
          <TextField label="End Date" type="date" value={range[1]} onChange={(e) => handleDateChange(e.target.value, 1)} fullWidth InputProps={{ inputProps: { min: range[0], max: null } }} />
        </Paper>
      </Popper>
    </Box>
  );
}
export function DateRangeField4({ name, label, value, onChange, error }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(value || ['', '']);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDateChange = (date, index) => {
    const newRange = [...range];
    newRange[index] = date;
    setRange(newRange);
    onChange(newRange);
  };

  return (
    <Box>
      <Typography>{label}</Typography>
      <TextField value={range[0] && range[1] ? `${range[0]} - ${range[1]}` : ''} onClick={handleOpen} fullWidth readOnly error={!!error} helperText={error} />
      <Popper open={open} anchorEl={anchorEl} placement="bottom-start">
        <Paper sx={{ p: 2, display: 'flex', gap: 2 }}>
          <Box>
            <Typography>Start Date</Typography>
            <TextField type="date" value={range[0]} onChange={(e) => handleDateChange(e.target.value, 0)} fullWidth InputLabelProps={{ shrink: true }} />
          </Box>
          <Box>
            <Typography>End Date</Typography>
            <TextField type="date" value={range[1]} onChange={(e) => handleDateChange(e.target.value, 1)} fullWidth InputLabelProps={{ shrink: true }} />
          </Box>
          <Box>
            <Button onClick={handleClose} variant="contained" color="primary">
              Apply
            </Button>
          </Box>
        </Paper>
      </Popper>
    </Box>
  );
}
export function DateRangeField3({ name, label, value, onChange, error }) {
  const [startDate, endDate] = value || ['', ''];

  const handleStartChange = (e) => {
    onChange([e.target.value, endDate]);
  };

  const handleEndChange = (e) => {
    onChange([startDate, e.target.value]);
  };

  return (
    <Box>
      <Typography>{label}</Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center'
        }}>
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={handleStartChange}
          error={!!error}
          fullWidth
          InputLabelProps={{
            shrink: true
          }}
        />
        <Typography>-</Typography>
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={handleEndChange}
          error={!!error}
          fullWidth
          InputLabelProps={{
            shrink: true
          }}
        />
      </Box>
      {error && <Typography color="error">{error}</Typography>}
    </Box>
  );
}
export function DateRangeField2({ name, label, value, onChange, error }) {
  const [startDate, endDate] = value || ['', ''];

  const handleStartChange = (e) => {
    onChange([e.target.value, endDate]);
  };

  const handleEndChange = (e) => {
    onChange([startDate, e.target.value]);
  };

  return (
    <Box>
      <Typography>{label}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={handleStartChange}
            error={!!error}
            fullWidth
            InputLabelProps={{
              shrink: true
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={handleEndChange}
            error={!!error}
            fullWidth
            InputLabelProps={{
              shrink: true
            }}
          />
        </Grid>
      </Grid>
      {error && <Typography color="error">{error}</Typography>}
    </Box>
  );
}

// Sub-component for file upload
export function FileUploader({ name, label, value, onChange, error, multiple = false }) {
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    onChange(multiple ? files : files[0]);
  };

  return (
    <div>
      <label>{label}</label>
      <Button variant="contained" component="label" style={{ marginTop: '8px' }}>
        Upload File
        <input hidden type="file" multiple={multiple} onChange={handleFileChange} />
      </Button>
      {error && <Typography color="error">{error}</Typography>}
    </div>
  );
}

// Sub-component for signature input
export function SignatureField({ name, label, value, onChange, error }) {
  return (
    <Box>
      <label htmlFor={name}>{label}</label>
      <div style={{ border: '1px solid #ccc', height: '150px', width: '100%', marginTop: '8px' }}>{/* Placeholder for signature canvas */}</div>
      {error && <Typography color="error">{error}</Typography>}
    </Box>
  );
}

// Sub-component for radio button groups
export function RadioButtonGroup({ name, label, options, value, onChange, columnsPerRow = 1 }) {
  return (
    <Grid container>
      <Grid item xs={12}>
        <label>{label}</label>
      </Grid>
      <Grid item xs={12}>
        <RadioGroup value={value} onChange={(e) => onChange(e.target.value)} row>
          {options.map((option, idx) => (
            <FormControlLabel key={idx} value={option.value} control={<Radio />} label={option.label} />
          ))}
        </RadioGroup>
      </Grid>
    </Grid>
  );
}

// Sub-component for toggle switches
export function ToggleSwitch({ name, label, value, onChange }) {
  return <FormControlLabel control={<Switch checked={value} onChange={(e) => onChange(e.target.checked)} />} label={label} />;
}

// Sub-component for checkbox fields
export function CheckboxField({ name, label, value, onChange, error }) {
  return (
    <Box>
      <FormControlLabel control={<Checkbox checked={value} onChange={(e) => onChange(e.target.checked)} name={name} />} label={label} />
      {error && <Typography color="error">{error}</Typography>}
    </Box>
  );
}
// Sub-component for auto-select fields
export function AutoSelectField({ name, label, options, value, onChange, error }) {
  return (
    <Box>
      <Autocomplete
        options={options}
        getOptionLabel={(option) => option.label || ''}
        value={options.find((option) => option.value === value) || null}
        onChange={(event, newValue) => onChange(newValue ? newValue.value : '')}
        renderInput={(params) => <TextField {...params} label={label} error={!!error} helperText={error} fullWidth variant="outlined" />}
        renderOption={(props, option) => (
          <Box component="li" {...props} display="flex" alignItems="center">
            {option.icon && <Box mr={1}>{option.icon}</Box>}
            <Typography>{option.label}</Typography>
          </Box>
        )}
      />
    </Box>
  );
}
